import DatadogHandler from 'utils/datadog';
import { actionHandlerWithFeatureFlag, retrieveSortForMS } from 'utils';
import {
  FEATURE_FLAGS,
  DEFAULT_ERROR,
  DEFAULT_PAGE,
  DEFAULT_ITEMS_PER_PAGE,
} from 'utils/constants';
import { msHandleWithdrawalManualFormComplete } from 'services/RegistrationsService';
import {
  msGetWithdrawalDefaultRefundAmount,
  msGetAllWithdrawalDrafts,
  getWithdrawalDraftItems,
  getChildByIds,
  getWithdrawalDraftActivityLogs,
} from 'services/ChildService';
import { transformWithdrawalDraftsForGraphQL } from 'utils/ms-utils/ms-phase-3/ChildUtils';
import {
  getFormParticipantsByFormIds,
  getFormsByFilter,
} from 'services/FormService';
import { getUserByIds, getRolesByUserIds } from 'services/UserService';
import { childService, MS_PHASE_3_ENDPOINTS } from 'utils/msEndpointConstants';
import { getCodeByCodeIds } from 'services/SchoolConfigService';
import { get, compact, uniq, keyBy, flatMap, isEmpty, isArray } from 'lodash';
import SkRequest from './BaseRequest';
import { QueryBuilder, MutationBuilder } from '../queryBuilder';
import {
  ALL_WITHDRAWAL_REQUEST,
  ALL_WITHDRAWAL,
  ALL_REFUNDS,
  FETCH_WITHDRAWAL_DRAFTS_LOADING,
  FETCH_WITHDRAWAL_DRAFTS_SUCCESS,
  FETCH_WITHDRAWAL_DRAFTS_ERROR,
} from './actionTypes';
import {
  GET_ALL_WITHDRAWAL_DRAFTS,
  CANCEL_COMPLETED_WITHDRAWAL,
  CHANGE_WITHDRAWAL_DATE,
  WITHDRAWAL_MANUAL_FORM_COMPLETE,
  TRANSFER_MANUAL_FORM_COMPLETE,
} from '../models/withdrawalModel';
import { SkApolloRequest } from '../../utils/apolloUtils';

const fileName = 'src/redux/actions/Withdrawal.js';

const _mQueryBuilder = new QueryBuilder();
const _mMutationEnquiryBuilder = new MutationBuilder();

_mMutationEnquiryBuilder.setNewMutation(
  'approveWithdrawalRequestByCentreStaff',
  {
    ID: 'number',
  }
);

_mMutationEnquiryBuilder.setNewMutation(
  'rejectWithdrawalRequestByCentreStaff',
  {}
);

_mMutationEnquiryBuilder.setNewMutation(
  'onAllWithdrawalDocumentedVerified',
  {}
);

_mMutationEnquiryBuilder.setNewMutation('approveWithdrawalRequestByHQ', {
  ID: 'number',
});

_mMutationEnquiryBuilder.setNewMutation('rejectWithdrawalRequestByHQ', {});

_mMutationEnquiryBuilder.setNewMutation('cancelWithdrawal', {});

_mMutationEnquiryBuilder.setNewMutation('cancelWithdrawalRequest', {});

_mQueryBuilder.setNewQuery('findAllWithdrawalDraft', {
  totalCount: 'number',
  data: {
    ID: 'number',
    fkSchool: 'number',
    fkCentre: 'number',
    status: 'string',
    eConsentStatus: 'string',
    updatedAt: 'Datetime',
    additionalStatus: 'string',
    forms: {
      data: {
        formParticipants: {
          data: {
            ID: 'number',
            status: 'string',
            email: 'string',
            createdAt: 'datetime',
            updatedAt: 'datetime',
            fkParent: 'number',
            user: {
              firstname: 'string',
              lastname: 'string',
              getRole: {
                label: 'string',
              },
            },
          },
        },
      },
    },
    estimateRefundAmount: {
      defaultDepositRefundAmount: 'number',
      defaultFeeRefundAmount: 'number',
      monthlyFee: 'number',
      depositAmount: 'number',
    },
    withdrawalDraftItem: {
      refundFeeAmount: 'number',
      refundDepositAmount: 'number',
      effectiveDate: 'Datetime',
      createdAt: 'Datetime',
      submissionDate: 'Datetime',
      otherReason: 'string',
      child: {
        ID: 'number',
        firstname: 'string',
        lastname: 'string',
      },
      codeByReason: {
        ID: 'number',
        label: 'string',
        description: 'string',
      },
      codeByParentReason: {
        ID: 'number',
        label: 'string',
        description: 'string',
      },
    },
    withdrawalDraftActivityLogs: {
      data: {
        ID: 'int',
        remark: 'string',
      },
    },
  },
});

_mQueryBuilder.setNewQuery('findAllWithdrawal', {
  totalCount: 'number',
  data: {
    ID: 'number',
    refundFeeAmount: 'number',
    refundDepositAmount: 'number',
    effectiveDate: 'Datetime',
    child: {
      ID: 'number',
      firstname: 'string',
      lastname: 'string',
    },
  },
});

_mQueryBuilder.setNewQuery('getWithdrawalDefaultRefundAmount', {
  depositAmount: 'number',
  monthlyFee: 'number',
  defaultFeeRefundAmount: 'number',
  defaultDepositRefundAmount: 'number',
});

const withdrawalDraftsLoading = () => ({
  type: FETCH_WITHDRAWAL_DRAFTS_LOADING,
  key: 'allWithdrawalDrafts',
  value: {
    inProgress: true,
  },
});

const withdrawalDraftsSuccess = data => ({
  type: FETCH_WITHDRAWAL_DRAFTS_SUCCESS,
  key: 'allWithdrawalDrafts',
  value: {
    inProgress: false,
    data,
  },
});

const withdrawalDraftsError = error => ({
  type: FETCH_WITHDRAWAL_DRAFTS_ERROR,
  key: 'allWithdrawalDrafts',
  value: {
    inProgress: false,
    data: null,
    error,
  },
});

const dispatchFindAllWithDrawalDraft = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: ALL_WITHDRAWAL_REQUEST,
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchFindAllWithDrawal = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: ALL_WITHDRAWAL,
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchAllRefunds = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: ALL_REFUNDS,
    value: {
      inProgress,
      data,
      error,
    },
  });
};

export const fetchAllRefunds = reqData => async dispatch => {
  dispatchAllRefunds(dispatch);

  const data = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      dispatch,
      msHandler: msGetWithdrawalDefaultRefundAmount,
      monoHandler: () => {
        try {
          const query = _mQueryBuilder.getQueryAsString(
            'getWithdrawalDefaultRefundAmount',
            reqData
          );
          return SkRequest({
            data: {
              query,
            },
            method: 'POST',
          });
        } catch (ex) {
          DatadogHandler.addError(ex);
          DatadogHandler.sendLog(
            ex,
            {
              functionName: 'fetchAllRefunds',
              fileName,
            },
            'error'
          );
          return {
            success: false,
            error: ex.message,
          };
        }
      },
      extraData: reqData,
    }
  );

  if (data.success) {
    dispatchAllRefunds(dispatch, false, { data: data.data });
  } else {
    dispatchAllRefunds(dispatch, false, null, data.error);
  }
  return data;
};

const { CHILD: CHILD_P3 } = MS_PHASE_3_ENDPOINTS;

export const monoFetchAllWithDrawalDraft = async (_, reqData) => {
  try {
    const { filter, pagination } = reqData;

    const sanitizedFilter = Object.assign({}, filter);
    const filterKeys = Object.keys(sanitizedFilter);

    filterKeys.forEach(eachFilter => {
      if (!sanitizedFilter[eachFilter]) {
        delete sanitizedFilter[eachFilter];
      }
    });

    const queryData = { filter: sanitizedFilter, pagination };
    if (!queryData.pagination) {
      delete queryData.pagination;
    }

    const query = _mQueryBuilder.getQueryAsString(
      'findAllWithdrawalDraft',
      queryData
    );

    const data = await SkRequest({
      data: {
        query,
      },
      method: 'POST',
    });

    return data;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoFetchAllWithDrawalDraft',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: ex.message,
    };
  }
};

export const msFetchAllWithDrawalDraft = async (_, reqData) => {
  try {
    const { filter, pagination, IDCentres, fkChild } = reqData;

    const normalizeFilterValues = value => {
      if (value === undefined || value === null) {
        return null;
      }
      if (Array.isArray(value)) {
        return value.filter(Boolean);
      }
      if (typeof value === 'string' && !value.trim()) {
        return null;
      }
      return [value];
    };

    const statusFilters = normalizeFilterValues(get(filter, 'status')) || [];
    const additionalStatusFilters = normalizeFilterValues(
      get(filter, 'additionalStatus')
    );

    const requestBody = {
      ids: get(filter, 'ids') || get(filter, 'IDs') || [],
      fk_centres: IDCentres || [],
      fk_child: fkChild,
      status: statusFilters,
    };

    if (additionalStatusFilters?.length) {
      requestBody.additional_status = additionalStatusFilters;
    }

    const resp = await childService().post(
      CHILD_P3.getWithdrawalDrafts,
      requestBody,
      {
        params: {
          page: pagination?.page || DEFAULT_PAGE,
          perPage: pagination?.perPage || DEFAULT_ITEMS_PER_PAGE,
          ...retrieveSortForMS(get(pagination, 'sort'), 'sort'),
        },
      }
    );

    const msData = get(resp, 'data.data', []);
    const totalCount = get(resp, 'data.totalCount', 0);

    if (isEmpty(msData)) {
      return {
        success: true,
        data: {
          totalCount: 0,
          data: [],
        },
      };
    }

    const draftIds = compact(uniq(msData.map(item => get(item, 'id'))));
    const childIds = compact(uniq(msData.map(item => get(item, 'fk_child'))));

    const [withdrawalDraftItems, children, withdrawalDraftActivityLogs] =
      await Promise.all([
        getWithdrawalDraftItems({ draftIds }),
        getChildByIds(childIds),
        getWithdrawalDraftActivityLogs(draftIds),
      ]);

    const withdrawalDraftItemsByIds = keyBy(
      isArray(withdrawalDraftItems) ? withdrawalDraftItems : [],
      'fk_draft'
    );
    const childrenById = keyBy(isArray(children) ? children : [], 'id');

    const activityLogsByDraftId = {};
    if (isArray(withdrawalDraftActivityLogs)) {
      withdrawalDraftActivityLogs.forEach(log => {
        const draftId = get(log, 'fk_draft');
        if (draftId) {
          if (!activityLogsByDraftId[draftId]) {
            activityLogsByDraftId[draftId] = [];
          }
          activityLogsByDraftId[draftId].push(log);
        }
      });
    }

    const forms = await getFormsByFilter({
      fk_withdrawal_draft: draftIds.map(id => ({ eq: id })),
    });

    const formsArray = isArray(forms) ? forms : [];
    const formsByDraftId = keyBy(formsArray, 'fk_withdrawal_draft');
    const formIds = compact(uniq(formsArray.map(form => get(form, 'id'))));

    const formParticipants = await getFormParticipantsByFormIds(formIds);

    const participantsFlatted = flatMap(
      Object.values(formParticipants || {}),
      participant => get(participant, 'data', [])
    );

    const userIds = compact(
      uniq(participantsFlatted.map(p => get(p, 'fk_user')))
    );

    const [users, userRoles] = await Promise.all([
      getUserByIds(userIds),
      getRolesByUserIds(userIds),
    ]);

    const usersById = keyBy(isArray(users) ? users : [], 'id');

    const reasonIds = compact(
      uniq(
        flatMap(Object.values(withdrawalDraftItemsByIds), item => [
          get(item, 'reason'),
          get(item, 'parent_reason'),
        ]).filter(Boolean)
      )
    );
    const codesById = await getCodeByCodeIds(reasonIds);

    const refundAmountPromises = msData.map(async item => {
      const draftId = get(item, 'id');
      const childId = get(item, 'fk_child');
      const withdrawalDraftItem = withdrawalDraftItemsByIds[draftId];

      if (!withdrawalDraftItem || !childId) {
        return { draftId, refundAmount: null };
      }

      const effectiveDate = get(withdrawalDraftItem, 'effective_date');
      const submissionDate = get(withdrawalDraftItem, 'submission_date');
      const isGraduation = get(withdrawalDraftItem, 'is_graduation', false);
      const isTransfer = get(withdrawalDraftItem, 'is_transfer', false);

      if (!effectiveDate || !submissionDate) {
        return { draftId, refundAmount: null };
      }

      const result = await msGetWithdrawalDefaultRefundAmount(null, {
        IDChild: childId,
        effectiveDate,
        is_graduation: isGraduation,
        is_transfer: isTransfer,
        withdrawalCreatedAt: submissionDate,
      });

      if (result.success) {
        return {
          draftId,
          refundAmount: get(
            result,
            'data.getWithdrawalDefaultRefundAmount',
            null
          ),
        };
      }

      return { draftId, refundAmount: null };
    });

    const refundAmountsResults = await Promise.all(refundAmountPromises);
    const refundAmountsByDraftId = keyBy(refundAmountsResults, 'draftId');

    const transformedData = transformWithdrawalDraftsForGraphQL({
      msData,
      withdrawalDraftItemsByIds,
      childrenById,
      formsByDraftId,
      formParticipants,
      codesById,
      usersById,
      userRoles,
      refundAmountsByDraftId,
      activityLogsByDraftId,
    });

    return {
      success: true,
      data: {
        findAllWithdrawalDraft: {
          data: transformedData,
          totalCount,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllWithDrawalDraft',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const fetchAllWithDrawalDraft =
  (filter, pagination = null) =>
  async dispatch => {
    dispatchFindAllWithDrawalDraft(dispatch);

    const sanitizedFilter = Object.assign({}, filter);
    const filterKeys = Object.keys(sanitizedFilter);

    filterKeys.forEach(eachFilter => {
      if (!sanitizedFilter[eachFilter]) {
        delete sanitizedFilter[eachFilter];
      }
    });

    const reqData = { filter: sanitizedFilter, pagination };
    if (!reqData.pagination) {
      delete reqData.pagination;
    }

    try {
      const data = await actionHandlerWithFeatureFlag(
        FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
        {
          dispatch,
          msHandler: msFetchAllWithDrawalDraft,
          monoHandler: monoFetchAllWithDrawalDraft,
          extraData: reqData,
        }
      );

      if (data.success) {
        dispatchFindAllWithDrawalDraft(dispatch, false, data.data);
      } else {
        dispatchFindAllWithDrawalDraft(dispatch, false, null, data.error);
      }
    } catch (ex) {
      dispatchFindAllWithDrawalDraft(dispatch, false, null, {
        error: ex.message,
      });
    }
  };

export const fetchAllWithDrawal =
  (filter, pagination = null) =>
  async dispatch => {
    dispatchFindAllWithDrawal(dispatch);

    const sanitizedFilter = Object.assign({}, filter);
    const filterKeys = Object.keys(sanitizedFilter);

    filterKeys.forEach(eachFilter => {
      if (!sanitizedFilter[eachFilter]) {
        delete sanitizedFilter[eachFilter];
      }
    });

    const reqData = { filter: sanitizedFilter, pagination };
    if (!reqData.pagination) {
      delete reqData.pagination;
    }

    const query = _mQueryBuilder.getQueryAsString('findAllWithdrawal', reqData);

    try {
      const data = await SkRequest({
        data: {
          query,
        },
        method: 'POST',
      });
      if (data.success) {
        dispatchFindAllWithDrawal(dispatch, false, data.data);
      } else {
        dispatchFindAllWithDrawal(dispatch, false, null, data.error);
      }
    } catch (ex) {
      dispatchFindAllWithDrawal(dispatch, false, null, { error: ex.message });
    }
  };

export const cancelWithdrawal = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'cancelWithdrawal',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const cancelWithdrawalRequest = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'cancelWithdrawalRequest',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const approveWithdrawalRequestByHQ = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'approveWithdrawalRequestByHQ',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const onAllWithdrawalDocumentedVerified = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'onAllWithdrawalDocumentedVerified',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const rejectWithdrawalRequestByPrincipal = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'rejectWithdrawalRequestByCentreStaff',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const rejectWithdrawalRequestByHQ = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'rejectWithdrawalRequestByHQ',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const approveWithdrawalByPrincipal = reqData => async () => {
  try {
    const data = await SkRequest({
      data: {
        query: _mMutationEnquiryBuilder.getMutationAsString(
          'approveWithdrawalRequestByCentreStaff',
          reqData
        ),
      },
      method: 'POST',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const monoGetAllWithdrawalDrafts = async (_, reqData) => {
  try {
    const data = await SkApolloRequest({
      params: {
        query: GET_ALL_WITHDRAWAL_DRAFTS,
        variables: reqData,
      },
      type: 'query',
    });
    return data;
  } catch (ex) {
    throw ex;
  }
};

export const getAllWithdrawalDrafts = reqData => async dispatch => {
  dispatch(withdrawalDraftsLoading());
  try {
    const data = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msGetAllWithdrawalDrafts,
        monoHandler: monoGetAllWithdrawalDrafts,
        extraData: reqData,
      }
    );
    if (data.success) {
      dispatch(withdrawalDraftsSuccess(data.data));
    } else {
      dispatch(withdrawalDraftsError(data.error));
    }
  } catch (ex) {
    dispatch(
      withdrawalDraftsError(ex?.ex?.message || ex?.message || DEFAULT_ERROR)
    );
  }
};

export const cancelCompletedWithdrawal = reqData => async () => {
  try {
    const data = await SkApolloRequest({
      params: {
        mutation: CANCEL_COMPLETED_WITHDRAWAL,
        variables: reqData,
      },
      type: 'mutation',
    });

    return data;
  } catch (ex) {
    return ex;
  }
};

export const changeWithdrawalDate = reqData => async () => {
  try {
    const data = await SkApolloRequest({
      params: {
        mutation: CHANGE_WITHDRAWAL_DATE,
        variables: reqData,
      },
      type: 'mutation',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export const monoHandleWithdrawalManualFormComplete = async (_, reqData) => {
  try {
    const { IDDraft, IDChild } = reqData;
    return SkApolloRequest({
      params: {
        mutation: WITHDRAWAL_MANUAL_FORM_COMPLETE,
        variables: { IDDraft, IDChild },
      },
      type: 'mutation',
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoHandleWithdrawalManualFormComplete',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: ex.message,
    };
  }
};

export const handleWithdrawalManualFormComplete =
  (IDDraft, IDChild) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msHandleWithdrawalManualFormComplete,
        monoHandler: monoHandleWithdrawalManualFormComplete,
        extraData: { IDDraft, IDChild },
      }
    );
  };

export const handleTransferManualFormComplete = IDDraft => async () => {
  try {
    const data = await SkApolloRequest({
      params: {
        mutation: TRANSFER_MANUAL_FORM_COMPLETE,
        variables: { IDDraft },
      },
      type: 'mutation',
    });
    return data;
  } catch (ex) {
    return ex;
  }
};

export default approveWithdrawalByPrincipal;
